// api.js
// All API calls to FastAPI backend
// Single place to change API URL if needed

import axios from 'axios';
//import useStore from './store';

import API_BASE from './config';

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
});

// Simple token store — avoids circular dependency with useStore
let _token = null;

export const setApiToken = (token) => { _token = token; };
export const clearApiToken = () => { _token = null; };

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  
  if (_token) {
    config.headers.Authorization = `Bearer ${_token}`;
  }
  return config;
});

// Redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      _token = null;
    }
    return Promise.reject(error);
  }
);

export const loginUser = async (username, password) => {
  const res = await axios.post(`${API_BASE}/auth/login`, {
    username,
    password,
  });
  return res.data;
};

// ============================================================
// CANDLES
// ============================================================

export const fetchCandles = async (symbol, timeframe, limit = 200, toTime = null) => {
  const params = { limit };
  if (toTime) params.to_time = toTime;
  const response = await api.get(
    `/candles/${symbol}/${timeframe}`,
    { params }
  );
  return response.data;
};

export const fetchCandlesByRange = async (
  symbol, timeframe, fromTime, toTime
) => {
  const response = await api.get(
    `/candles/${symbol}/${timeframe}`,
    { params: { from_time: fromTime, to_time: toTime } }
  );
  return response.data;
};

// Fetch N candles after a given time — right lazy load
export const fetchCandlesAfter = async (symbol, timeframe, fromTime, limit = 300) => {
  const response = await api.get(
    `/candles/${symbol}/${timeframe}`,
    { params: { from_time: fromTime, limit } }
  );
  return response.data;
};

// ============================================================
// INDICATORS
// ============================================================

export const fetchIndicators = async (symbol, timeframe, limit = 300, toTime = null, fromTime = null) => {
  const params = { limit };
  if (toTime)   params.to_time   = toTime;
  if (fromTime) params.from_time = fromTime;
  const response = await api.get(
    `/indicators/${symbol}/${timeframe}`,
    { params }
  );
  return response.data;
};

export const fetchIndicatorsByRange = async (symbol, timeframe, fromTime, toTime = null) => {
  // Fetch indicators between fromTime and toTime
  // If toTime is null, fetches from fromTime to present
  const params = { from_time: fromTime };
  if (toTime) params.to_time = toTime;
  const response = await api.get(
    `/indicators/${symbol}/${timeframe}`,
    { params }
  );
  return response.data;
};

// ============================================================
// WATCHLIST
// Uses /watchlists/default/stocks endpoint
// Returns { watchlist: [], has_default: false } if none set
// ============================================================

export const fetchWatchlist = async () => {
  const response = await api.get('/watchlists/default/stocks');
  return {
    watchlist:   response.data.symbols || [],
    has_default: response.data.has_default || false,
  };
};

export const fetchAllWatchlists = async () => {
  const response = await api.get('/watchlists');
  return response.data;
};

export const fetchWatchlistStocks = async (watchlistId) => {
  const response = await api.get(`/watchlists/${watchlistId}/stocks`);
  return response.data;
};

export const setDefaultWatchlist = async (watchlistId) => {
  const response = await api.put(
    `/watchlists/${watchlistId}/set-default`
  );
  return response.data;
};

// ============================================================
// STOCKS
// ============================================================

export const fetchStocks = async () => {
  const response = await api.get('/stocks');
  return response.data;
};

// ============================================================
// TRADES
// ============================================================

// Fetch filter options (years, instruments, symbols)
export const fetchTradeFilters = async () => {
  const response = await api.get('/trades/filters');
  return response.data;
};

// Fetch trades for chart markers — all trades for a symbol
export const fetchTradesForChart = async (symbol) => {
  const response = await api.get(`/trades/chart/${symbol}`);
  return response.data;
};

// Fetch trades with filters for review view
export const fetchTrades = async (filters = {}) => {
  const response = await api.get('/trades', { params: filters });
  return response.data;
};

// Fetch single trade by id
export const fetchTradeById = async (tradeId) => {
  const response = await api.get(`/trades/${tradeId}`);
  return response.data;
};

// Update remarks and rating during review
export const updateTradeReview = async (tradeId, remarks, rating) => {
  const response = await api.put(
    `/trades/${tradeId}/review`,
    null,
    { params: { remarks, rating } }
  );
  return response.data;
};

// Upload tradebook file (xlsx/csv/tsv) — multipart
export const uploadTradebook = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post('/trades/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};


// ============================================================
// LIVE CANDLE — current open candle from WebSocket feed
// ============================================================

export const fetchLiveCandle = async (symbol, timeframe) => {
  const response = await api.get(`/live/${symbol}/${timeframe}`);
  return response.data;
};

// ============================================================
// CROSSOVERS & V PATTERNS
// ============================================================

export const fetchCrossoverFilters = async () => {
  const res = await api.get('/crossovers/filters');
  return res.data;
};

export const fetchCrossovers = async (filters = {}) => {
  const res = await api.get('/crossovers', { params: filters });
  return res.data;
};

export const fetchVPatterns = async (filters = {}) => {
  const res = await api.get('/v_patterns', { params: filters });
  return res.data;
};

export const fetchVPatternsForChart = async (symbol, timeframe, filters = {}) => {
  const res = await api.get(`/v_patterns/chart/${symbol}/${timeframe}`, {
    params: filters,
  });
  return res.data;
};

export const fetchVPatternFilters = async () => {
  const res = await api.get('/v_patterns/filters');
  return res.data;
};

export const fetchVPatternDefaults = async () => {
  const res = await api.get('/settings/vpattern_defaults');
  return res.data;
};

export const saveVPatternDefaults = async (defaults) => {
  const res = await api.post('/settings/vpattern_defaults', defaults);
  return res.data;
};

export const createTempWatchlist = async (symbols) => {
  const res = await api.post('/watchlists/temporary', null, {
    params: { symbols: symbols.join(',') }
  });
  return res.data;
};

export const startCrossoverCalculation = async (params = {}) => {
  const res = await api.post('/crossovers/calculate', null, { params });
  return res.data;
};

export const startVPatternCalculation = async (params = {}) => {
  const res = await api.post('/v_patterns/calculate', null, { params });
  return res.data;
};

// ============================================================
// HEALTH
// ============================================================

export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

// ============================================================
// LIVE ALERTS
// ============================================================

export const fetchAlertsToday = async () => {
  const res = await api.get('/alerts/today');
  return res.data;
};

export const deleteAlertHistory = async (period = 'all') => {
  const res = await api.delete('/alerts/history', { params: { period } });
  return res.data;
};

export const fetchAlertSettings = async () => {
  const res = await api.get('/alerts/settings');
  return res.data;
};

export const saveAlertSettings = async (config) => {
  const res = await api.post('/alerts/settings', config);
  return res.data;
};

// ============================================================
// EMA STRATEGY BACKTEST
// ============================================================
export const runEmaStrategy = async (payload) => {
  const res = await api.post('/ema-strategy/run', payload);
  return res.data;
};

export const downloadEmaStrategyExcel = async (params) => {
  const res = await api.get('/ema-strategy/excel', {
    params,
    responseType: 'blob',
  });
  return res.data;
};